import { WEBAPP_URL } from '@src/constants'
import { parseServerDate } from '@helpers/utils'

// Only two states reach the UI: the pending-invites endpoint already filters
// out accepted invites (`used_at`), and revoked ones are deleted outright.
export type InviteStatus = 'sent' | 'expired'

/**
 * The minimum an invite confirmation modal needs.
 *
 * Narrower than `InviteUser` on purpose: the Access-column chip only has a
 * uuid + email off the org-structure row, and typing the modals to the full
 * invite payload would have forced that surface to keep its own parallel
 * resend/revoke implementation instead of reusing them.
 */
export type InviteRef = {
  uuid: string
  email: string
}

/**
 * Classify an invite by its expiry timestamp.
 *
 * An unknown/unparseable expiry reads as `sent`. Of the two possible wrong
 * answers that's the safer one: labelling a live invite "Expired" pushes the
 * admin to re-send an invitation the person can already use, while the reverse
 * just leaves the row looking as it does today.
 */
export const getInviteStatus = (expiresAt?: string | null, now: Date = new Date()): InviteStatus => {
  const expires = parseServerDate(expiresAt)
  if (!expires) {
    return 'sent'
  }
  // Strictly `<`, matching the backend gate (`expires_at < utcnow()` in
  // InviteDetail.get): at exactly `expires_at` the join link still works, so
  // the row must not be painted red while the invitee can still use it.
  return expires.getTime() < now.getTime() ? 'expired' : 'sent'
}

/**
 * Rebuild the join link the invite email carries.
 *
 * The backend composes `${FRONTEND_URL_SIGNUP}?invite=<uuid>` and never
 * returns it, but the UUID is on the invite payload, so the link is
 * reconstructable client-side. WEBAPP_URL rather than window.location.origin:
 * inside the MS Teams embed the origin is the -teams host, which isn't a valid
 * signup domain (same reasoning as the team join link in Settings).
 */
export const buildInviteLink = (uuid: string) => `${WEBAPP_URL}/signup?invite=${uuid}`
